import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	IconButton,
	Paper,
	TableContainer,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Table,
	Typography,
	Stack,
	Button,
	Tooltip
} from '@mui/material';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { isEmpty, isUndefined } from 'lodash';

import { getRuta, isAccess, simpleMessage } from '../../helpers/Helpers';
import { getToken } from '../../api/Account';
import { DataContext } from '../../context/DataContext';
import { deleteClientAsync, getClientListAsync } from '../../api/Clients';
import NoData from '../../components/NoData';
import SearchField from '../../components/ui/SearchField';
import ListFooter from '../../components/ui/ListFooter';
import Swal from 'sweetalert2';
import { monoFont, getTokens } from '../../theme';

const initials = (fullName) => {
	const parts = (fullName || '').trim().split(/\s+/);
	return `${(parts[0] || '')[0] || ''}${(parts[1] || '')[0] || ''}`.toUpperCase();
};

const ClientList = () => {
	const [clientList, setClientList] = useState([]);
	const [localReload, setLocalReload] = useState(false);

	const { setIsLoading, setIsUnautorized, access, selectedSucursal, isDarkMode } =
		useContext(DataContext);
	const t = getTokens(isDarkMode);

	let ruta = getRuta();
	const token = getToken();
	let navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState('');

	const withSearch = clientList.filter((val) => {
		if (searchTerm === '') {
			return val;
		} else if (
			val.id.toString().includes(searchTerm) ||
			(val.fullName || '').toString().includes(searchTerm) ||
			(val.identification || '').toString().includes(searchTerm)
		) {
			return val;
		}
		return false;
	});

	useEffect(() => {
		if (selectedSucursal === 0 || isUndefined(selectedSucursal)) {
			return;
		}
		getClients(selectedSucursal);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSucursal, localReload]);

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsperPage] = useState(10);
	const indexLast = currentPage * itemsperPage;
	const indexFirst = indexLast - itemsperPage;
	const currentItem = withSearch.slice(indexFirst, indexLast);
	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	const onChangeSearch = (val) => {
		setCurrentPage(1);
		setSearchTerm(val.toUpperCase());
		paginate(1);
	};

	const deleteClient = async (item) => {
		Swal.fire({
			title: '¿Estas Seguro?',
			text: 'No podras revertir esto...!',
			icon: 'warning',
			showCancelButton: true,

			confirmButtonColor: '#1d4ed8',
			cancelButtonColor: '#d83a3f',

			confirmButtonText: 'Eliminar!',
			cancelButtonText: 'Cancelar',
			customClass: {
				popup: 'border'
			}
		}).then((result) => {
			if (result.isConfirmed) {
				(async () => {
					setIsLoading(true);
					const result = await deleteClientAsync(token, item.id);
					if (!result.statusResponse) {
						setIsLoading(false);
						if (result.error === 'Unauthorized') {
							setIsUnautorized(true);
							return;
						}
						simpleMessage(result.error, 'error');
						return;
					}

					setIsLoading(false);
					setLocalReload(!localReload);

					Swal.fire('Eliminado!', 'Cliente Eliminado.', 'success');
				})();
			}
		});
	};

	const getClients = async (sucursal) => {
		setIsLoading(true);
		const result = await getClientListAsync(token, sucursal);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setClientList(result.data.result);
	};

	const actionButtonSx = {
		width: 32,
		height: 32,
		borderRadius: '9px',
		border: `1px solid ${t.fieldLine}`,
		backgroundColor: t.field
	};

	const puedeAccionar = isAccess(access, 'CLIENTS UPDATE') || isAccess(access, 'CLIENTS DELETE');

	return (
		<div>
			<Paper sx={{ overflow: 'hidden' }}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent="space-between"
					alignItems={{ xs: 'stretch', sm: 'center' }}
					spacing={2}
					flexWrap="wrap"
					sx={{ padding: '18px 22px', borderBottom: '1px solid', borderColor: 'divider' }}
				>
					<SearchField
						value={searchTerm}
						onChange={onChangeSearch}
						placeholder="Buscar por nombre, cédula o #…"
					/>

					{isAccess(access, 'CLIENTS CREATE') ? (
						<Button
							onClick={() => navigate(`${ruta}/addclient`)}
							startIcon={<PersonAddRoundedIcon />}
							variant="contained"
							color="primary"
							sx={{ whiteSpace: 'nowrap', height: '44px' }}
						>
							Agregar cliente
						</Button>
					) : null}
				</Stack>

				{isEmpty(currentItem) ? (
					<NoData />
				) : (
					<>
						<TableContainer sx={{ overflowX: 'auto' }}>
							<Table sx={{ minWidth: 720 }}>
								<TableHead>
									<TableRow>
										<TableCell align="center">#</TableCell>
										<TableCell>Nombre completo</TableCell>
										<TableCell>Cédula</TableCell>
										<TableCell align="center">Teléfono</TableCell>
										{puedeAccionar ? <TableCell align="center">Acciones</TableCell> : null}
									</TableRow>
								</TableHead>
								<TableBody>
									{currentItem.map((row) => (
										<TableRow
											key={row.id}
											hover
											onClick={() => navigate(`${ruta}/client/${row.id}`)}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell align="center" sx={{ color: 'text.disabled', fontWeight: 600 }}>
												{row.id}
											</TableCell>
											<TableCell>
												<Stack direction="row" spacing={1.3} alignItems="center">
													<Box
														sx={{
															width: 34,
															height: 34,
															borderRadius: '10px',
															backgroundColor: 'rgba(18,163,90,.13)',
															color: '#12a35a',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontFamily: monoFont,
															fontWeight: 600,
															fontSize: 12,
															flexShrink: 0
														}}
													>
														{initials(row.fullName)}
													</Box>
													<Typography
														sx={{
															fontSize: 13.5,
															fontWeight: 700,
															color: 'text.primary',
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis'
														}}
													>
														{row.fullName}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell sx={{ color: 'text.secondary' }}>{row.identification}</TableCell>
											<TableCell align="center" sx={{ color: 'text.secondary' }}>
												{row.phone}
											</TableCell>

											{puedeAccionar ? (
												<TableCell align="center" onClick={(e) => e.stopPropagation()}>
													<Stack direction="row" spacing={1} justifyContent="center">
														{isAccess(access, 'CLIENTS UPDATE') ? (
															<Tooltip title="Ver detalle">
																<IconButton
																	size="small"
																	sx={{ ...actionButtonSx, color: 'primary.main' }}
																	onClick={() => navigate(`${ruta}/client/${row.id}`)}
																>
																	<VisibilityRoundedIcon sx={{ fontSize: 15 }} />
																</IconButton>
															</Tooltip>
														) : null}
														{isAccess(access, 'CLIENTS DELETE') ? (
															<Tooltip title="Eliminar">
																<IconButton
																	size="small"
																	sx={{ ...actionButtonSx, color: 'error.main' }}
																	onClick={() => deleteClient(row)}
																>
																	<DeleteRoundedIcon sx={{ fontSize: 15 }} />
																</IconButton>
															</Tooltip>
														) : null}
													</Stack>
												</TableCell>
											) : null}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<ListFooter
							data={withSearch}
							currentCount={currentItem.length}
							indexFirst={indexFirst}
							paginate={paginate}
							itemsperPage={itemsperPage}
							noun="clientes"
						/>
					</>
				)}
			</Paper>
		</div>
	);
};

export default ClientList;
